/**
 * Small adapter around the CookieScript browser API.
 *
 * CookieScript uses these categories:
 *   - performance: analytics/measurement
 *   - targeting: advertising, remarketing and enhanced-conversion user data
 *   - functionality/strict: first-party site operation
 *
 * Consent is considered unknown until CookieScript exposes currentState().
 * Treating an unknown state as denied prevents events or identifiers from
 * being sent before the CMP has initialized.
 */

const COOKIE_SCRIPT_EVENTS = [
  "CookieScriptLoaded",
  "CookieScriptAcceptAll",
  "CookieScriptAccept",
  "CookieScriptReject",
  "CookieScriptConsentUpdated",
  "CookieScriptCategory-performance",
  "CookieScriptCategory-targeting",
  "CookieScriptCategory-functionality",
];

const ALL_CATEGORIES = ["strict", "performance", "targeting", "functionality", "unclassified"];

const EMPTY_STATE = Object.freeze({
  known: false,
  action: undefined,
  categories: Object.freeze([]),
  analytics: false,
  marketing: false,
});

function isBrowser() {
  return typeof window !== "undefined";
}

function getCookieScriptState(detail) {
  if (!isBrowser()) return null;

  const instance = window.CookieScript?.instance;
  let state = null;
  try {
    if (instance && typeof instance.currentState === "function") {
      state = instance.currentState();
    }
  } catch {
    state = null;
  }

  // CookieScriptAccept includes the selected categories in event.detail. This
  // fallback also makes the adapter work during the same tick as the event,
  // before currentState() has been refreshed internally.
  if (!state && detail && typeof detail === "object") state = detail;
  if (!state || !Array.isArray(state.categories)) return null;

  const categories = state.categories.filter((category) => typeof category === "string");
  return {
    known: true,
    action: state.action,
    categories,
    analytics: categories.includes("performance"),
    marketing: categories.includes("targeting"),
  };
}

export function getConsentState() {
  return getCookieScriptState() || EMPTY_STATE;
}

export function hasConsent(purpose) {
  const state = getConsentState();
  if (purpose === "analytics") return state.known && state.analytics;
  if (purpose === "marketing") return state.known && state.marketing;
  if (purpose === "functionality") return state.known && state.categories.includes("functionality");
  return false;
}

/**
 * Subscribe to CookieScript changes. The callback receives the normalized
 * state and can clear first-party attribution when consent is revoked.
 */
export function subscribeConsent(callback) {
  if (!isBrowser() || typeof callback !== "function") return () => {};

  const notify = (event) => {
    // The generic reject/accept-all events do not always carry detail and, on
    // slower browsers, currentState() may update one tick later. Use the
    // event semantics as a safe fallback for that transition.
    if (event?.type === "CookieScriptReject") {
      callback({
        known: true,
        action: "reject",
        categories: ["strict"],
        analytics: false,
        marketing: false,
      });
      return;
    }
    if (event?.type === "CookieScriptAcceptAll") {
      callback({
        known: true,
        action: "accept",
        categories: ALL_CATEGORIES,
        analytics: true,
        marketing: true,
      });
      return;
    }
    callback(getCookieScriptState(event?.detail) || EMPTY_STATE);
  };
  COOKIE_SCRIPT_EVENTS.forEach((name) => window.addEventListener(name, notify));

  // CookieScript can already be initialized before Gatsby hydrates.
  const initial = getCookieScriptState();
  if (initial) callback(initial);

  return () => {
    COOKIE_SCRIPT_EVENTS.forEach((name) => window.removeEventListener(name, notify));
  };
}
